<template>
  <div>
    <v-container class="pt-3 pb-6 px-6">
      <!-- Floating Button -->
      <floating-button @click="reportCreate()" v-if="authUser && !isClient" />

      <!-- Confirmation -->
      <confirmation
        v-if="authUser"
        v-model="dialog"
        title="Eliminar Informe"
        text="Desea eliminar el informe"
        :item="itemToDelete.wt_wo_report_id ? 'v.' + itemToDelete.id : ''"
        @close="dialog = false"
        @confirm="confirmDelete()"
      />

      <!-- Page Header -->
      <page-header
        title="Informes"
        icon="mdi-file-document-edit"
        divider="-"
        :breadcrumbs="breadcrumbs.items"
        refresh
        @refresh="init(true)"
      ></page-header>

      <!-- Content -->
      <v-row align="center" justify="center">
        <!-- Table -->
        <v-col cols="12">
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <v-data-table
            v-show="authUser"
            :headers="headers"
            :items="items"
            :items-per-page="5"
            class="elevation-1"
            :expanded.sync="expanded"
            :options.sync="pagination"
            :server-items-length="pagination_total"
            :footer-props="{
              'items-per-page-options': [5, 15, 30, 50]
            }"
            @update:options="init()"
          >
            <!-- Top -->
            <template v-slot:top>
              <v-toolbar flat color="white">
                <!-- State Filter -->
                <v-select
                  v-if="!isClient"
                  v-model="filters.state_id"
                  :items="reportStates"
                  class="shrink mr-5"
                  label="Estado"
                  clearable
                  hide-details
                  single-line
                  @change="init()"
                ></v-select>
                <!-- Work Type Filter -->
                <v-select
                  v-if="!isClient"
                  v-model="filters.work_type_id"
                  :items="workTypes"
                  class="shrink mr-5"
                  label="Tipo de trabajo"
                  clearable
                  hide-details
                  single-line
                  @change="init()"
                ></v-select>
                <!-- Farm Filter -->
                <v-autocomplete
                  v-model="filters.wind_farm_id"
                  :items="farms"
                  label="Parque"
                  :class="isClient ? 'shrink' : 'grow'"
                  class="mr-5"
                  item-text="name"
                  item-value="id"
                  clearable
                  single-line
                  hide-details
                  @change="init()"
                ></v-autocomplete>
                <!-- Work Orders -->
                <v-autocomplete
                  v-if="!isClient"
                  v-model="filters.work_order_id"
                  :items="work_orders"
                  label="Código OT"
                  class="grow mr-5"
                  item-text="code"
                  item-value="id"
                  clearable
                  single-line
                  hide-details
                  @change="init()"
                ></v-autocomplete>
              </v-toolbar>
            </template>
            <!-- Interventions -->
            <template v-slot:item.work_interventions="{ item }">
              {{
                item.work_interventions.map(element => element.id).join(", ")
              }}
            </template>
            <!-- Language -->
            <template v-slot:item.language="{ item }">
              {{ language[item.language] }}
            </template>
            <!-- Updated at -->
            <template v-slot:item.updated_at="{ item }">
              {{ formatDate(item.updated_at) }}
            </template>
            <!-- State -->
            <template v-slot:item.state="{ item }">
              <v-row justify="center" align="center" align-center>
                <v-chip
                  :class="{ 'white--text': item.state_id != 1 }"
                  :color="reportStateColor[item.state_id]"
                >
                  <span>{{ reportState[item.state_id] }}</span>
                </v-chip>
              </v-row>
            </template>
            <!-- Actions -->
            <template v-slot:item.action="{ item }">
              <v-icon class="mr-2" @click="reportEdit(item.id)">
                {{ isClient ? "mdi-eye" : "mdi-pencil" }}
              </v-icon>
              <v-icon v-if="!isClient && !isReadonly" @click="deleteDialog(item, false)">mdi-delete</v-icon>
            </template>
            <!-- Expand Icon -->
            <template v-slot:item.expand="{ item }">
              <v-icon
                :class="{ rotate: expanded.includes(item) }"
                icon
                @click="
                  expanded.includes(item)
                    ? (expanded = [])
                    : (expanded = [item])
                "
                >mdi-chevron-down</v-icon
              >
            </template>
            <!-- Expanded Row -->
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <!-- Headers -->
                <v-row
                  justify="space-around"
                  class="text-center font-weight-bold caption"
                >
                  <v-col cols="2">Versión</v-col>
                  <v-col cols="2">Tipo</v-col>
                  <v-col cols="2">Fecha modificación</v-col>
                  <v-col cols="3">Estado</v-col>
                  <v-col cols="2">Acciones</v-col>
                </v-row>
                <v-row
                  justify="space-around"
                  class="text-center"
                  v-for="(version, idx) in item.report_versions"
                  :key="idx"
                >
                  <!-- Divider -->
                  <v-col cols="12" class="py-0">
                    <v-divider />
                  </v-col>
                  <!-- Version -->
                  <v-col cols="2">
                    v.{{ item.report_versions.length - idx }}
                  </v-col>
                  <!-- Report Type -->
                  <v-col cols="2">
                    {{ reportType[version.report_type] }}
                  </v-col>
                  <!-- Date -->
                  <v-col cols="2">
                    {{ formatDate(version.updated_at) }}
                  </v-col>
                  <!-- State -->
                  <v-col cols="3">
                    <v-chip
                      :class="{ 'white--text': version.state_id != 1 }"
                      :color="reportStateColor[version.state_id]"
                    >
                      {{ reportState[version.state_id] }}
                    </v-chip>
                  </v-col>
                  <!-- Actions -->
                  <v-col cols="2">
                    <v-icon
                      class="mr-2"
                      :disabled="version.state_id != 3"
                      @click="download(version.download_url)"
                      >mdi-download</v-icon
                    >
                    <v-icon
                      v-if="!isClient"
                      @click="deleteDialog(version, item.id)"
                      >mdi-delete</v-icon
                    >
                  </v-col>
                </v-row>
              </td>
            </template>
            <!-- No Data -->
            <template v-slot:no-data>
              No hay elementos
            </template>
            <!-- No Results -->
            <template v-slot:no-results>
              No hay elementos
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import Confirmation from "@/components/Confirmation";
import PageHeader from "@/components/PageHeader";
import Util from "@/util/index";
import STATES from "@/models/States";

export default {
  components: {
    FloatingButton,
    Confirmation,
    PageHeader
  },
  data: () => ({
    expanded: [],
    authUser: false,
    loadFilters: true,
    isLoadingData: false,
    dialog: false,
    reportStates: STATES.REPORT_STATES,
    reportState: STATES.REPORT_STATE,
    reportStateColor: STATES.REPORT_STATE_COLOR,
    language: STATES.REPORT_LANGUAGE,
    reportType: STATES.REPORT_TYPE,
    workTypes: STATES.WORK_ORDER_TYPES,
    wind_turbines: [],
    itemToDelete: {},
    filters: {
      state_id: "",
      work_type_id: "",
      work_order_id: "",
      wind_farm_id: ""
    },
    pagination: {
      page: 1,
      itemsPerPage: 5,
      sortBy: [],
      sortDesc: []
    },
    pagination_total: -1,
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "Informes", disabled: true }
      ]
    },
    defaultHeaders: [
      {
        text: "",
        value: "expand",
        sortable: false,
        align: "center",
        client: true
      },
      { text: "Código OT", value: "work_order.code", align: "center" },
      { text: "Intervenciones", value: "work_interventions", align: "center" },
      {
        text: "Tipo de informe",
        value: "work_order.work_type",
        align: "center"
      },
      {
        text: "Parque",
        value: "wind_farm.name",
        align: "center",
        client: true
      },
      { text: "AG", value: "wind_turbine", align: "center", client: true },
      { text: "Idioma", value: "language", align: "center" },
      {
        text: "Fecha últ. mod.",
        value: "updated_at",
        align: "center",
        client: true
      },
      { text: "Estado", value: "state", align: "center" },
      {
        text: "Acciones",
        value: "action",
        sortable: false,
        align: "center"
      }
    ]
  }),
  computed: {
    isClient() {
      return this.$store.getters["IS_CLIENT"];
    },
    isReadonly() {
      return this.$store.getters['IS_READONLY']
    },
    loading: {
      get() {
        return this.$store.state.loading;
      },
      set(val) {
        this.$store.commit("loading", val);
      }
    },
    headers() {
      return this.isClient
        ? this.defaultHeaders.filter(e => e.client)
        : this.defaultHeaders;
    },
    items() {
      return this.$store.state.reports.list;
    },
    farms() {
      return this.$store.state.farms.wind_farms.filter(element => {
        return element.is_active === true;
      });
    },
    work_orders() {
      return this.$store.state.orders.work_orders;
    }
  },
  methods: {
    download(url) {
      window.open(url, "_blank");
    },
    formatDate(date) {
      return Util.formatDate(date);
    },
    deleteDialog(item) {
      this.itemToDelete = item;
      this.dialog = true;
    },
    reportCreate() {
      this.$router.push({ name: "report-create" });
    },
    reportEdit(id) {
      this.$router.push({
        name: "report-edit",
        params: { id: id }
      });
    },
    confirmDelete() {
      this.itemToDelete.wt_wo_report_id
        ? this.deleteVersion()
        : this.deleteReport();
    },
    deleteReport() {
      this.dialog = false;
      this.loading = true;
      const body = {
        report_id: this.itemToDelete.id
      };
      this.$store
        .dispatch("reports/delete", body)
        .then(() => {
          this.$toast("Informe borrado correctamente.", {
            color: "success",
            x: "center",
            y: "top",
            icon: "mdi-information"
          });
          this.init();
        })
        .catch(error => {
          var errorToastMessage = "Ha ocurrido un error eliminando el informe.";
          Util.showErrorToast(this, error, errorToastMessage);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deleteVersion() {
      this.dialog = false;
      this.loading = true;
      const body = {
        version_id: this.itemToDelete.id,
        report_id: this.itemToDelete.wt_wo_report_id
      };
      this.$store
        .dispatch("reports/deleteVersion", body)
        .then(() => {
          this.$toast("Informe borrado correctamente.", {
            color: "success",
            x: "center",
            y: "top",
            icon: "mdi-information"
          });
          this.init();
        })
        .catch(error => {
          var errorToastMessage = "Ha ocurrido un error eliminando el informe.";
          Util.showErrorToast(this, error, errorToastMessage);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    init(loadFilters) {
      this.isLoadingData = true;
      console.log("init -> true", true);
      const body = {
        page: this.pagination.page,
        perPage: this.pagination.itemsPerPage,
        filters: this.filters,
        with: "workOrder,windFarm,workInterventions,reportVersions"
      };
      // Sort
      if (this.pagination.sortDesc.length)
        body.sortType = this.pagination.sortDesc[0] ? "desc" : "asc";
      if (this.pagination.sortBy.length) {
        const sortBy = this.pagination.sortBy[0];
        if (sortBy === "wind_farm.name") body.sortBy = "wind_farm";
        else if (sortBy === "work_order.work_type") body.sortBy = "work_type";
        else if (sortBy === "work_order.code") body.sortBy = "work_order_code";
        else body.sortBy = sortBy;
      }
      this.$store
        .dispatch("reports/list", body)
        .then(res => {
          this.authUser = true;
          const meta = res.data.meta;
          this.pagination_total = meta.total;
        })
        .catch(error => {
          if (error.response.status === 403) {
            this.$toast(
              "Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.",
              {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              }
            );
            this.$router.push({ name: "home" });
          } else {
            var errorToastMessage =
              "Ha ocurrido un error cargando los informes.";
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          if (this.loadFilters || loadFilters) this.loadFarms();
          else if (!this.loadFilters) this.isLoadingData = false;
          this.loadFilters = false;
        });
    },
    loadFarms() {
      this.$store
        .dispatch("farms/getFarms")
        .catch(error => {
          if (error.response.status === 403) {
            this.$toast(
              "Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.",
              {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              }
            );
            this.$router.push({ name: "home" });
          } else {
            var errorToastMessage =
              "Ha ocurrido un error cargando la lista parques eólicos.";
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          if (!this.isClient) this.loadWorkOrders();
          else this.isLoadingData = false;
        });
    },
    loadWorkOrders() {
      this.$store
        .dispatch("orders/getOrders")
        .catch(error => {
          if (error.response.status === 403) {
            this.$toast(
              "Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.",
              {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              }
            );
            this.$router.push({ name: "home" });
          } else {
            var errorToastMessage =
              "Ha ocurrido un error cargando la lista de órdenes de trabajo.";
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.isLoadingData = false;
        });
    }
  },
  created() {
    // this.init()
  }
};
</script>
